import React, { useEffect, useRef, useState } from "react";

import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  Slide,
  Switch,
  TextField,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { formatISO } from "date-fns";
import {
  compact,
  debounce,
  find,
  get,
  mapValues,
  snakeCase,
  upperFirst,
} from "lodash";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import HeightIcon from "@/components/Icons/HeightIcon";
import WeightIcon from "@/components/Icons/WeightIcon";
import { graphql } from "@/gql";
import {
  BodyMeasurementAttributes,
  BodyMeasurementFieldsKind,
  BodyMeasurementListItem_SettingFragment,
  CreateBodyMeasurementsInput,
  UpdateSettingsInput,
} from "@/gql/graphql";
import { useSnackBar } from "@/hooks/useSnackBar";
import { colors } from "@/theme/overrides/colors";
import { formatISODate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";

import { bodyMeasurementsHistoryTableQuery } from "./BodyMeasurementHistoryTable";
import UploadPhotoModal from "./UploadPhotoModal";
import { clientProfilePageQuery } from "../../../ClientProfilePage";

interface AddBodyMeasurementModalType {
  loading?: boolean;
  isEditing: boolean;
  onClose: () => void;
  visibility: boolean;
  settings?: BodyMeasurementListItem_SettingFragment | null;
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    alignItems: "center",
    justifyContent: "center",
    overflowY: "auto",
  },
  buttonContainer: {
    display: "flex",
    marginTop: "auto",
    alignSelf: "center",
    marginBottom: "20px",
  },
  customizeModalOpen: {
    marginRight: theme.spacing(0),
    marginLeft: theme.spacing(16),
  },
  customizeModalClose: {
    marginRight: "auto",
    marginLeft: "auto",
  },
  customizeModal: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#EDF0F5",
    marginTop: theme.spacing(4),
    marginRight: theme.spacing(16),
    padding: theme.spacing(1, 4),
    width: "40%",
    overflowY: "auto",
  },
  defaultBodyMeasurements: {
    display: "flex",
    backgroundColor: colors.white,
    flexDirection: "column",
    marginBottom: theme.spacing(2),
  },
  defaultBodyMeasurementsHeaderText: {
    margin: theme.spacing(2),
  },
  error: {
    fontSize: theme.typography.pxToRem(12),
  },
  fields: {
    marginBottom: 12,
  },
  fieldWrapper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  fieldNames: {
    fontSize: theme.typography.pxToRem(14),
    color: colors.grey,
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: colors.white,
    padding: theme.spacing(2, 4),
    width: "60%",
    marginTop: theme.spacing(4),
  },
  modalHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: theme.spacing(8),
  },
  modalHeaderExpanded: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  modalHeaderText: {
    fontWeight: "bold",
  },
  switchWrapper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginLeft: theme.spacing(4),
  },
  uploadBox: {
    backgroundColor: "#EDF0F5",
    height: 230,
    width: 200,
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    alignSelf: "center",
    color: colors.grey,
    height: "24px",
    width: "24px",
  },
  scaleTextField: {
    marginBottom: 20,
  },
  scaleTextFieldLabel: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: "bold",
    marginBottom: 10,
  },
  scaleTextFieldIconContainer: {
    background: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: 50,
    height: 48,
    width: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  scaleTextFieldInputContainer: {
    marginLeft: 15,
  },
  scaleTextFieldInput: {
    "&::placeholder": {
      fontSize: 24,
      fontWeight: 700,
      color: theme.palette.grey[400],
    },
  },
  imageContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    height: 142,
    width: 175,
    backgroundColor: colors.white,
  },
  image: {
    height: "100%",
    width: "100%",
    objectFit: "contain",
  },
}));

const StyledButton = withStyles(Button, {
  root: {
    border: "1px solid #FFF",
  },
});

const StyledRestorButton = withStyles(Button, (theme: Theme) => ({
  root: {
    alignSelf: "center",
    margin: theme.spacing(4),
  },
}));

const StyledTextField = withStyles(TextField, {
  root: {
    border: "1px solid #DEE6F0",
  },
});

const StyledCancelButton = withStyles(Button, {
  root: {
    color: "#FF6262",
    border: "2px solid #FF6262",
    marginRight: 8,
  },
});

const StyledFormControlLabel = withStyles(FormControlLabel, (theme: Theme) => ({
  label: {
    color: colors.grey,
    fontSize: theme.typography.pxToRem(12),
  },
  root: {
    marginBottom: 8,
  },
}));

export interface BodyMeasurementFields {
  name: string;
  label: string;
  placeholder?: string;
  type: string;
  class?: string;
  unitLabel?: string;
  icon?: JSX.Element | null;
  visibility: boolean;
  value: number | null;
}

const BODY_MEASUREMENT_FIELDS: { [key: string]: string } = mapValues(
  BodyMeasurementFieldsKind
);

const hasUpTo2Decimals = function (value: number | null | undefined): boolean {
  if (!value) return true;
  if (Number.isInteger(value)) return true;
  const result = value.toString().split(".")[1]
    ? value.toString().split(".")[1].length <= 2
    : true;
  return result;
};

export type Image = {
  title: string;
  signedId: string;
  imageUrl: string;
  id?: string;
  markedForDestruction: boolean;
};

type FormValues = {
  date: Date;
  images: Image[] | [];
  bodyMeasurementFields: BodyMeasurementFields[];
};

const bodyMeasurementByDateQuery = graphql(`
  query BodyMeasurementByDate($id: ID!, $date: ISO8601Date!) {
    client(id: $id) {
      id
      bodyMeasurementByDate(date: $date) {
        ...BodyMeasurementListItem
      }
    }
  }
`);

const clientBodyMeasurementSettingQuery = graphql(`
  query ClientBodyMeasurementSetting($id: ID!) {
    client(id: $id) {
      id
      setting {
        ...BodyMeasurementListItem_Setting
      }
    }
  }
`);

const createBodyMeasurementsMutation = graphql(`
  mutation CreateBodyMeasurements($input: CreateBodyMeasurementsInput!) {
    createBodyMeasurements(input: $input) {
      bodyMeasurement {
        ...BodyMeasurementListItem
      }
    }
  }
`);

const updateBodyMeasurementsSettingMutation = graphql(`
  mutation UpdateBodyMeasurementsSetting($input: UpdateSettingsInput!) {
    updateSettings(input: $input) {
      settings {
        ...BodyMeasurementListItem_Setting
      }
    }
  }
`);

const AddBodyMeasurementModal: React.FC<AddBodyMeasurementModalType> = ({
  onClose,
  visibility,
  settings,
  isEditing,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const { clientId } = useParams<{ clientId: string }>();
  const [_updateBodyMeasurementsSetting] = useMutation(
    updateBodyMeasurementsSettingMutation
  );
  const [_createBodyMeasurements] = useMutation(createBodyMeasurementsMutation);
  const [SimpleSnackBar, snackBarHelper] = useSnackBar();
  const [fetch, { data, loading, refetch }] = useLazyQuery(
    bodyMeasurementByDateQuery,
    {
      variables: {
        id: clientId ?? "",
        date: formatISODate(new Date()),
      },
    }
  );

  useEffect(() => {
    if (visibility) fetch();
  }, [visibility]);

  const [customizeModalVisible, setCustomizeModalVisible] =
    useState<boolean>(false);
  const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false);
  const [editImageTitleIndex, setEditImageTitleIndex] =
    useState<number | null>(null);
  const [isSavingSelection, setIsSavingSelection] = useState<boolean>(false);
  const getVisibility = (fieldName: string): boolean => {
    return (
      find(settings?.bodyMeasurementFields, { fieldName: snakeCase(fieldName) })
        ?.visible ?? false
    );
  };
  const bodyMeasurementFieldsVisibilityInitialValue = [
    {
      name: "weightKg",
      label: "Current weight",
      placeholder: "Enter weight",
      visibility: true,
      type: "defaultField",
      class: "scale",
      unitLabel: "kg",
      icon: <WeightIcon />,
      value: 0,
    },
    {
      name: "abdominalGirthCm",
      label: "Abdominal girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "heightCm",
      label: "Height",
      visibility: true,
      placeholder: "Enter height",
      type: "defaultField",
      class: "scale",
      unitLabel: "cm",
      icon: <HeightIcon />,
      value: 0,
    },
    {
      name: "ankleGirthCm",
      label: "Ankle girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "armFlexGirthCm",
      label: "Arm Flex Girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "armRelaxedGirthCm",
      label: "Arm Relaxed Girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "calfGirthCm",
      label: "Calf Girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "chestGirthCm",
      label: "Chest Girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "forearmGirthCm",
      label: "Forearm girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "headCircumferenceCm",
      label: "Head Circumference",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "hipCircumferenceCm",
      label: "Hip Circumference",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "neckGirthCm",
      label: "Neck Girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "shoulderGirthCm",
      label: "Shoulder Girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "thighGirthCm",
      label: "Thigh Girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "waistCircumferenceCm",
      label: "Waist Circumference",
      visibility: true,
      type: "defaultField",
      value: 0,
    },
    {
      name: "wristGirthCm",
      label: "Wrist Girth",
      visibility: true,
      type: "defaultField",
      value: 0,
    },

    // Additional fields start here

    {
      name: "abdominalSkinfoldMm",
      label: "Abdominal Skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "medicalCalfSkinfoldMm",
      label: "Medial Calf Skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "abdominalSubcutaneousFatThicknessMm",
      label: "Abdominal Subcutaneous Fat Thickness",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "subscapularSkinfoldMm",
      label: "Subscapular skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "ankleBreadthCm",
      label: "Ankle breadth",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "suprailiacSkinfoldMm",
      label: "Suprailiac skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "armPerimeterCm",
      label: "Arm Perimeter",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "supraspinalSkinfoldMm",
      label: "Supraspinal skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "bicepsSkinfoldMm",
      label: "Biceps Skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "tricepSkinfoldMm",
      label: "Tricep Skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "chestSkinfoldMm",
      label: "Chest Skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "wristBreadthCm",
      label: "Wrist breadth",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "elbowBreadthMm",
      label: "Elbow breadth",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "fiveSkinfoldSum",
      label: "5 skinfold sum",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "femurBreadthMm",
      label: "Femur breadth",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "waistToHipRatio",
      label: "Waist to Hip Ratio",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "frontThighSkinfoldMm",
      label: "Front Thigh Skinfold",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
    {
      name: "eightSkinfoldSum",
      label: "8 skinfold sum",
      visibility: false,
      type: "additionalField",
      value: 0,
    },
  ];

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
    control,
    reset,
    getValues,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      date: new Date(),
      bodyMeasurementFields: bodyMeasurementFieldsVisibilityInitialValue,
      images: [],
    },
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const controlledDate = watch("date");

  const { fields } = useFieldArray({ control, name: "bodyMeasurementFields" });
  const { fields: imagesFields } = useFieldArray({ control, name: "images" });

  const date = useRef(controlledDate);

  useEffect(() => {
    if (date.current.getTime() !== controlledDate.getTime()) {
      debounce(async () => {
        const result = await refetch({
          date: formatISO(controlledDate, { representation: "date" }),
          id: clientId,
        });
        if (result) return;
      }, 1000)();
    }
    return () => {
      date.current = controlledDate;
    };
  }, [controlledDate]);

  useEffect(() => {
    let fetching = true;
    if (fetching) {
      const bodyMeasurementsValues = data?.client?.bodyMeasurementByDate;
      if (bodyMeasurementsValues) {
        const newValues = fields.map((bm) => ({
          ...bm,
          visibility: getVisibility(bm.name),
          value: get(bodyMeasurementsValues, bm.name),
        }));
        setValue("bodyMeasurementFields", [...newValues]);
        if (isEditing) {
          const newImages =
            bodyMeasurementsValues.images?.map((image) => ({
              title: image.title ?? "",
              imageUrl: image.imageUrl ?? "",
              signedId: image.signedId ?? "",
              id: image.bodyMeasurementId,
              markedForDestruction: false,
            })) ?? [];
          setValue("images", newImages);
        } else {
          setValue("images", []);
        }
      }
    }
    return () => {
      fetching = false;
    };
  }, [
    settings?.bodyMeasurementFields,
    data?.client?.bodyMeasurementByDate,
    isEditing,
  ]);

  const createBodyMeasurements = async (input: CreateBodyMeasurementsInput) => {
    return await _createBodyMeasurements({
      variables: { input },
      refetchQueries: [
        { query: clientProfilePageQuery, variables: { id: clientId ?? "" } },
        {
          query: bodyMeasurementsHistoryTableQuery,
          variables: { id: clientId ?? "" },
        },
      ],
      awaitRefetchQueries: true,
    });
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    const date = formatISO(values.date, { representation: "date" });

    const bodyMeasurements = values.bodyMeasurementFields.map((bm) => {
      if (bm.value && bm.visibility) {
        return {
          attr: snakeCase(bm.name),
          value: Number(bm.value),
          date: date,
        };
      }
    });

    const images = values.images.map((image: Image) => {
      if (image.id) {
        if (image.markedForDestruction) {
          return {
            id: image.id,
            _destroy: true,
          };
        }
        return {
          id: image.id,
          attr: "image",
          date: date,
          imageAttributes: { title: image.title },
        };
      }
      return {
        attr: "image",
        date: date,
        imageAttributes: { title: image.title, image: image.signedId },
      };
    });

    const attributes: BodyMeasurementAttributes[] = [
      ...images,
      ...compact(bodyMeasurements),
    ];

    const input: CreateBodyMeasurementsInput = {
      clientId: clientId,
      attributes: [...compact(attributes)],
    };

    try {
      const result = await createBodyMeasurements(input);

      if (result.data) {
        snackBarHelper.openSnackBar(
          `Successfully ${isEditing ? "updated" : "added"}`
        );
        onClose();
      }
    } catch (e) {
      snackBarHelper.openSnackBar(
        displayError(
          e,
          "There was an error in updating your clients body measurement"
        )
      );
    }
  };

  const handleUpdateClientSettings = async () => {
    setIsSavingSelection(true);
    const bodyMeasurementFields: BodyMeasurementFieldsKind[] = compact(
      fields.map((f) => {
        if (f.visibility)
          return BODY_MEASUREMENT_FIELDS[
            upperFirst(f.name)
          ] as BodyMeasurementFieldsKind;
      })
    );

    const input: UpdateSettingsInput = {
      clientId: clientId,
      attributes: {
        bodyMeasurementFields: bodyMeasurementFields,
      },
    };

    try {
      await _updateBodyMeasurementsSetting({
        variables: { input },
        refetchQueries: [
          {
            query: clientBodyMeasurementSettingQuery,
            variables: { id: clientId ?? "" },
          },
          {
            query: bodyMeasurementsHistoryTableQuery,
            variables: { id: clientId ?? "" },
          },
        ],
      });
    } catch (e) {
      snackBarHelper.openSnackBar(
        displayError(e, "There was an error in saving your fields selection")
      );
    } finally {
      setIsSavingSelection(false);
      snackBarHelper.openSnackBar("Selection saved.");
    }
  };

  const addImage = (newImage: Image) => {
    const images = getValues("images");
    const newImages = [...images, newImage];
    setValue("images", newImages);
  };

  const deleteImage = (signedId: string) => {
    const images = getValues("images");
    const newImages = images.map((image: Image) => {
      if (image.signedId === signedId)
        return { ...image, markedForDestruction: true };
      return image;
    });
    setValue("images", newImages);
  };

  function handleRestoreDefault() {
    const defaultSettings = fields.map((element) => {
      if (element.type === "defaultField") {
        return { ...element, visibility: true };
      } else {
        return { ...element, visibility: false };
      }
    });
    setValue("bodyMeasurementFields", [...defaultSettings]);
  }

  function getErrorMessage(type: string | undefined) {
    switch (type) {
      case "max":
        return "Value for this field have exceeded 100";
      case "validate":
        return "Value must not exceed 2 decimal places";
      case "min":
        return "Must be a minimum value of 0";
      default:
        return "Please input a valid number";
    }
  }

  function displayBodyMeasurementFields(type: string) {
    return fields.map((field, index) => {
      if (field.visibility && field.type === type && field?.class !== "scale") {
        return (
          <Grid
            item
            xs={12}
            md={4}
            xl={3}
            className={classes.fields}
            key={field.id}
          >
            <Typography className={classes.fieldNames}>
              {field.label}
            </Typography>
            <StyledTextField
              key={field.id}
              type={"number"}
              inputProps={{ step: "0.01" }}
              size="small"
              fullWidth
              {...register(`bodyMeasurementFields.${index}.value` as const, {
                required: false,
                validate: hasUpTo2Decimals,
              })}
            />
            {errors.bodyMeasurementFields !== undefined
              ? errors.bodyMeasurementFields[index] && (
                  <Typography
                    key={index}
                    variant="subtitle2"
                    color="error"
                    className={classes.error}
                  >
                    {getErrorMessage(
                      errors.bodyMeasurementFields[index]?.value?.type
                    )}
                  </Typography>
                )
              : null}
          </Grid>
        );
      } else if (field.visibility && field.class == type) {
        return (
          <Box key={field.id} style={{ width: "30%", marginRight: "12px" }}>
            <Box
              className={classes.scaleTextField}
              display="flex"
              alignItems="center"
            >
              <Box className={classes.scaleTextFieldIconContainer}>
                {field.icon}
              </Box>
              <Box className={classes.scaleTextFieldInputContainer}>
                <Typography
                  className={classes.scaleTextFieldLabel}
                  color="textSecondary"
                  gutterBottom
                >
                  {field.label}
                </Typography>
                <TextField
                  variant="standard"
                  size="small"
                  type="number"
                  placeholder={field.placeholder}
                  InputProps={{
                    classes: {
                      input: classes.scaleTextFieldInput,
                    },
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {field.unitLabel}
                      </InputAdornment>
                    ),
                  }}
                  {...register(
                    `bodyMeasurementFields.${index}.value` as const,
                    {
                      required: false,
                      validate: hasUpTo2Decimals,
                    }
                  )}
                />
              </Box>
            </Box>
          </Box>
        );
      }
    });
  }

  function displayBodyComponentsSwitches(type: string) {
    return fields.map((field) => {
      if (field.type == type) {
        return (
          <Box
            display="flex"
            alignItems="flex-start"
            width="50%"
            key={field.id}
          >
            <StyledFormControlLabel
              value="end"
              control={
                <Switch
                  color="primary"
                  size="small"
                  checked={field.visibility}
                  onChange={(event) => {
                    const updatedValue = fields.map((e) => {
                      if (e.name === field.name) {
                        return { ...e, visibility: event.target.checked };
                      }
                      return e;
                    });
                    setValue("bodyMeasurementFields", [...updatedValue]);
                  }}
                />
              }
              label={field.label}
              labelPlacement="end"
            />
          </Box>
        );
      }
    });
  }

  function displayDateField() {
    return (
      <Box style={{ width: "30%" }}>
        <Box
          className={classes.scaleTextField}
          display="flex"
          alignItems="center"
        >
          <Box className={classes.scaleTextFieldInputContainer}>
            <Typography
              className={classes.scaleTextFieldLabel}
              color="textSecondary"
              gutterBottom
            >
              Date
            </Typography>
            <Controller
              control={control}
              name="date"
              render={({
                field: { ref, name, onChange, ...field },
                fieldState,
              }) => (
                <DatePicker
                  {...field}
                  inputRef={ref}
                  onChange={(value) => onChange(value as Date)}
                  slotProps={{
                    textField: {
                      name,
                      fullWidth: true,
                      error: !!fieldState?.error,
                      helperText: fieldState?.error?.message,
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  function displayImageFields() {
    return imagesFields.map((image, index) => {
      const images = getValues("images");
      const imageTitle = images[index].title;
      if (!image.markedForDestruction) {
        return (
          <Box
            key={index}
            component={Paper}
            padding={theme.spacing(0.5)}
            marginRight={theme.spacing(0.5)}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            maxWidth={200}
          >
            <Box
              className={classes.imageContainer}
              marginBottom={theme.spacing(0.5)}
            >
              <img className={classes.image} src={image.imageUrl} />
            </Box>
            <Box
              width="100%"
              alignSelf="flex-start"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <>
                <TextField
                  style={{
                    display: editImageTitleIndex === index ? "block" : "none",
                  }}
                  variant="standard"
                  size="small"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  key={index}
                  {...register(`images.${index}.title` as const, {
                    required: true,
                  })}
                />
              </>
              <Typography
                noWrap
                style={{
                  maxWidth: "50%",
                  display: editImageTitleIndex === index ? "none" : "block",
                }}
              >
                {imageTitle}
              </Typography>
              <Box display="flex" alignItems="center">
                {editImageTitleIndex !== null &&
                editImageTitleIndex === index ? (
                  <IconButton
                    onClick={() => setEditImageTitleIndex(null)}
                    size="small"
                  >
                    <CheckIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={() => setEditImageTitleIndex(index)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => deleteImage(image.signedId)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        );
      }
    });
  }

  if (loading) return null;

  return (
    <>
      <Modal
        open={visibility}
        onClose={() => {
          reset();
          onClose();
        }}
        className={classes.root}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box display="flex" flexDirection="row">
            <Box
              className={cx(classes.modalBody, {
                [classes.customizeModalOpen]: customizeModalVisible,
                [classes.customizeModalClose]: !customizeModalVisible,
              })}
            >
              <Box className={classes.modalHeader}>
                <Typography variant="h6" color="textPrimary">
                  {isEditing ? "Edit" : "Add"} Body Measurements
                </Typography>
                <div>
                  <StyledButton
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => setCustomizeModalVisible(true)}
                  >
                    Customize
                  </StyledButton>

                  <IconButton
                    onClick={() => {
                      onClose();
                      setCustomizeModalVisible(false);
                    }}
                    size="large"
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                {displayBodyMeasurementFields("scale")}
                {displayDateField()}
              </Box>
              {fields.length !== 0 ? (
                <>
                  <Box className={classes.fieldWrapper}>
                    <Grid container spacing={4}>
                      {displayBodyMeasurementFields("defaultField")}
                      {displayBodyMeasurementFields("additionalField")}
                    </Grid>
                  </Box>
                </>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  marginBottom={8}
                >
                  <Typography variant="h6" color="textSecondary">
                    There are no visible fields anymore. Click the customize
                    button to set which field will be visible.
                  </Typography>
                </Box>
              )}
              <Box
                width="100%"
                display="flex"
                flexWrap={"wrap"}
                alignItems="center"
                marginBottom={theme.spacing(2)}
              >
                <Box
                  className={classes.uploadBox}
                  component={ButtonBase}
                  onClick={() => setUploadModalVisible(true)}
                >
                  <ImageIcon className={classes.icon} />
                  <Typography variant="caption">Upload Body Image</Typography>
                </Box>

                {/* Images */}
                {displayImageFields()}
              </Box>
              <Box className={classes.buttonContainer}>
                <StyledCancelButton
                  variant="outlined"
                  onClick={() => {
                    onClose();
                    reset();
                  }}
                >
                  Cancel
                </StyledCancelButton>
                <Button
                  color="primary"
                  disabled={fields.length == 0 || !isValid}
                  type="submit"
                >
                  {isEditing ? "Save" : "Add"}
                </Button>
              </Box>
            </Box>

            <Slide
              direction="right"
              mountOnEnter
              unmountOnExit
              in={customizeModalVisible}
            >
              <Box className={classes.customizeModal}>
                <Box className={classes.modalHeaderExpanded}>
                  <Typography
                    variant="h6"
                    color="textPrimary"
                    style={{ alignSelf: "center" }}
                  >
                    Customize Measurements
                  </Typography>
                  <IconButton
                    onClick={() => setCustomizeModalVisible(false)}
                    size="large"
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Box className={classes.defaultBodyMeasurements}>
                  <Typography
                    variant="subtitle1"
                    color="textPrimary"
                    className={classes.defaultBodyMeasurementsHeaderText}
                  >
                    Default body measurements
                  </Typography>
                  <FormControl component="fieldset">
                    <FormGroup>
                      <Box className={classes.switchWrapper}>
                        {displayBodyComponentsSwitches("defaultField")}
                      </Box>
                    </FormGroup>
                  </FormControl>
                </Box>

                <Box className={classes.defaultBodyMeasurements}>
                  <Typography
                    variant="subtitle1"
                    color="textPrimary"
                    className={classes.defaultBodyMeasurementsHeaderText}
                  >
                    Additional Body Measurements
                  </Typography>
                  <FormControl component="fieldset">
                    <FormGroup>
                      <Box className={classes.switchWrapper}>
                        {displayBodyComponentsSwitches("additionalField")}
                      </Box>
                    </FormGroup>
                  </FormControl>
                </Box>

                <Box alignSelf={"center"}>
                  <Button
                    variant="contained"
                    onClick={handleUpdateClientSettings}
                    disabled={isSavingSelection}
                  >
                    Save Selection
                  </Button>
                  <StyledRestorButton
                    variant="outlined"
                    onClick={handleRestoreDefault}
                  >
                    Restore Default
                  </StyledRestorButton>
                </Box>
              </Box>
            </Slide>
          </Box>
        </form>
      </Modal>
      <UploadPhotoModal
        onAddImage={addImage}
        visible={uploadModalVisible}
        onClose={() => setUploadModalVisible(false)}
      />
      <SimpleSnackBar />
    </>
  );
};

export default AddBodyMeasurementModal;
